import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { SortableMenuCard } from './SortableMenuCard';
import { menuAPI } from '../../utils/api';
import { toast } from 'sonner';

export const MenuGrid = ({ 
  menuItems, 
  setMenuItems, 
  onEdit, 
  onDelete, 
  onDuplicate,
  onRefetch,
  selectionMode = false,
  selectedItems = [],
  setSelectedItems = () => {},
  reorderDisabled = false
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    if (selectionMode || reorderDisabled) return; // Disable drag when in selection mode or filtered view
    
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = menuItems.findIndex((item) => item.id === active.id);
    const newIndex = menuItems.findIndex((item) => item.id === over.id);
    
    const newItems = arrayMove(menuItems, oldIndex, newIndex);
    setMenuItems(newItems);

    const reorderData = newItems.map((item, index) => ({
      id: item.id,
      sort_order: index
    }));

    try {
      await menuAPI.reorderMenuItems(reorderData);
      toast.success('Menu order updated');
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to save order');
      onRefetch();
    }
  };

  const handleToggleSelect = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  if (menuItems.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl">
        <p className="text-lg font-medium text-muted-foreground">No menu items yet</p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={menuItems.map(item => item.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item) => (
            <SortableMenuCard
              key={item.id}
              item={item}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              selectionMode={selectionMode}
              isSelected={selectedItems.includes(item.id)}
              onToggleSelect={handleToggleSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
